/**
 * Automatic property dtype stabilization (ANALYTICS-6897).
 *
 * Sites feeding the SDK through window.dataLayer (or plain track() calls)
 * routinely send the same property with different JSON types across events:
 * `active_filter_count: false` on one page and `active_filter_count: 3` on
 * another, `amount: 12.5` here and `amount: "12.50"` there. The backend keys
 * its property registry on (name, dtype), so every extra type became a
 * duplicate "Event Properties" row.
 *
 * The declared schema (config.propertyTypes) already fixes this for keys the
 * integrator names; this registry covers everything else, with no config:
 *
 *  - Every scalar event_params value is classified by SHAPE: boolean (B),
 *    number (N), numeric-looking string "12.50" (NS), boolean-looking string
 *    "true" (BS) or free string (S).
 *  - The set of shapes seen per key is remembered (localStorage, so it
 *    survives page loads) and resolved to ONE output type by a fixed table.
 *    The resolution depends only on the SET of shapes, never on the order in
 *    which they arrived, so two devices that have seen the same shapes make
 *    the same decision and converge.
 *  - Values are converted to that output type at send time.
 *
 * Resolution table:
 *   any S                → string   (numbers/booleans become their String form)
 *   N or NS, with BS     → string
 *   N  (± NS ± B)        → number   false→0, true→1, "12.50"→12.5
 *   B + NS (no N)        → string   false→"false"
 *   NS alone / NS + BS   → string   (unchanged: a site that only ever sends
 *                                    "12345" keeps its strings)
 *   B  (± BS)            → boolean  "true"→true
 *   BS alone             → string   (unchanged)
 *
 * Invariant: the output type is always one the backend has ALREADY stored
 * for the key (N→int/float, B→boolean, NS/BS/S→string). A conversion can
 * therefore never add a new (name, dtype) row — only stop adding more.
 * That is why B + NS resolves to string, not number: neither shape was ever
 * sent as a number.
 *
 * Senders call observe() for the whole batch before apply(), so every event
 * in one batch leaves with the same type even when the odd one comes first.
 *
 * A key whose values always arrive with one shape is never converted, so
 * consistent integrations see no change. Keys declared in propertyTypes are
 * skipped (the declared type wins, enforced by EventUtils.enforceType). Junk
 * strings ("", "undefined", "null", "NaN") neither register a shape nor get
 * converted. Arrays and nulls pass through untouched.
 */

export const SHAPE = Object.freeze({
  B: 1,   // boolean
  N: 2,   // number
  NS: 4,  // numeric-looking string
  BS: 8,  // boolean-looking string
  S: 16   // free string
});

export const DEFAULT_STORAGE_KEY = 'b2m_prop_dtypes';
export const DEFAULT_MAX_KEYS = 500;

// Plain decimal / scientific notation only. Leading zeros ("007"), hex, and
// partial forms ("12.", ".5") are NOT numeric: a site sending those means
// them as codes, and converting would lose the leading zeros.
const NUMERIC_RE = /^[+-]?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/;
const JUNK = new Set(['', 'undefined', 'null', 'nan']);

/**
 * Whether a string is a number the SDK may safely convert (finite and within
 * the integer-precise range, so long ids never lose digits).
 * @param {string} s
 * @returns {boolean}
 */
export function isNumericString(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  if (!NUMERIC_RE.test(t)) return false;
  const n = Number(t);
  return Number.isFinite(n) && Math.abs(n) <= Number.MAX_SAFE_INTEGER;
}

/**
 * Classify one (post-coerceValue) value into a SHAPE bit.
 * @param {*} value
 * @returns {number} a SHAPE bit, or 0 when the value does not take part
 *   (null/undefined, arrays, objects, junk strings)
 */
export function classify(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'boolean') return SHAPE.B;
  if (typeof value === 'number') return Number.isFinite(value) ? SHAPE.N : 0;
  if (typeof value !== 'string') return 0;
  const t = value.trim();
  if (JUNK.has(t.toLowerCase())) return 0;
  if (isNumericString(t)) return SHAPE.NS;
  const l = t.toLowerCase();
  if (l === 'true' || l === 'false') return SHAPE.BS;
  return SHAPE.S;
}

/**
 * Output type for a set of observed shapes. Pure function of the mask, so
 * the arrival order of shapes never matters.
 * @param {number} mask OR-ed SHAPE bits
 * @returns {'number'|'boolean'|'string'}
 */
export function resolveType(mask) {
  if (mask & SHAPE.S) return 'string';
  const hasN = !!(mask & SHAPE.N);
  const hasNS = !!(mask & SHAPE.NS);
  const hasB = !!(mask & SHAPE.B);
  const hasBS = !!(mask & SHAPE.BS);
  if (hasN || hasNS) {
    if (hasBS) return 'string';
    if (hasN) return 'number';
    // NS alone, or NS + B: nothing was ever sent as a number, so do not
    // invent one — booleans join the existing string row instead.
    return 'string';
  }
  if (hasB) return 'boolean';
  return 'string';
}

/**
 * Convert a scalar to the resolved type. Only the conversions the table can
 * demand are performed; anything else is returned as-is.
 * @param {*} value
 * @param {'number'|'boolean'|'string'} type
 * @returns {*}
 */
export function convertTo(value, type) {
  switch (type) {
    case 'number':
      if (typeof value === 'number') return value;
      if (typeof value === 'boolean') return value ? 1 : 0;
      if (typeof value === 'string' && isNumericString(value)) return Number(value.trim());
      return value;
    case 'boolean': {
      if (typeof value === 'boolean') return value;
      if (typeof value !== 'string') return value;
      const l = value.trim().toLowerCase();
      if (l === 'true') return true;
      if (l === 'false') return false;
      return value;
    }
    case 'string':
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      return value;
    default:
      return value;
  }
}

export default class PropertyDtypeRegistry {
  /**
   * @param {object|null} storage  SDK Storage (get/set); null → memory only
   * @param {object} [options]
   * @param {string} [options.storageKey]
   * @param {number} [options.maxKeys]  cap on remembered keys; beyond it new
   *   keys pass through unchanged (a GTM-heavy dataLayer can carry hundreds)
   */
  constructor(storage, options = {}) {
    this.storage = storage || null;
    this.storageKey = options.storageKey || DEFAULT_STORAGE_KEY;
    this.maxKeys = options.maxKeys > 0 ? options.maxKeys : DEFAULT_MAX_KEYS;
    this._masks = null; // lazy: storage is read on first apply()
  }

  _load() {
    if (this._masks) return this._masks;
    let stored = null;
    try { stored = this.storage ? this.storage.get(this.storageKey) : null; } catch (e) { stored = null; }
    const masks = {};
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
      Object.keys(stored).forEach((k) => {
        const m = stored[k];
        if (typeof m === 'number' && m > 0 && m < 32) masks[k] = m;
      });
    }
    this._masks = masks;
    return masks;
  }

  _persist() {
    if (!this.storage) return;
    try { this.storage.set(this.storageKey, this._masks); } catch (e) { /* storage full / blocked */ }
  }

  /**
   * Record one value's shape for a key without converting anything.
   * @param {string} key
   * @param {*} value  post-coerceValue value
   * @param {object} [declared]  config.propertyTypes — declared keys are skipped
   * @returns {number} the key's mask after recording (0 when not tracked)
   */
  observe(key, value, declared) {
    if (declared && declared[key]) return 0;
    const shape = classify(value);
    const masks = this._load();
    const prev = masks[key] || 0;
    if (!shape) return prev;
    if (!prev && Object.keys(masks).length >= this.maxKeys) return 0;

    const mask = prev | shape;
    if (mask !== prev) {
      masks[key] = mask;
      this._persist();
    }
    return mask;
  }

  /**
   * Resolve one event_params value to the key's stable type, recording the
   * value's shape on the way.
   * @param {string} key
   * @param {*} value  post-coerceValue value
   * @param {object} [declared]  config.propertyTypes — declared keys are skipped
   * @returns {*}
   */
  apply(key, value, declared) {
    if (declared && declared[key]) return value;
    const mask = this.observe(key, value, declared);
    return mask ? convertTo(value, resolveType(mask)) : value;
  }

  /** Resolved type for a key, or null when the key has not been seen. */
  typeOf(key) {
    const mask = this._load()[key];
    return mask ? resolveType(mask) : null;
  }

  /** Copy of the remembered shape masks (for debugging / tests). */
  snapshot() {
    return { ...this._load() };
  }

  /** Forget every decision (memory + storage). */
  reset() {
    this._masks = {};
    this._persist();
  }
}
